import { useContext, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Stack, Typography } from '@mui/material'
import { InfoOutlined } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import ReadinessService from '../../../shared/services/readiness/readiness.service'
import { IReadinessFinding } from '../../../shared/services/readiness/readiness.model'
import { ReportPeriodContext } from '../../../context/report-periods/report-periods'

// Pre-flight for report generation: the same finding objects as the readiness page, surfaced
// where reports are generated. Warns honestly and never blocks: generating with open blocking
// findings is allowed, it just should not be a surprise.
export const ReadinessPreflightBanner = () => {
  const navigate = useNavigate()
  const readinessService = useMemo(() => new ReadinessService(), [])
  const reportPeriodContext = useContext(ReportPeriodContext)
  const reportPeriodId = reportPeriodContext.lastSelectedReportId

  const [blocking, setBlocking] = useState<IReadinessFinding[]>([])

  useEffect(() => {
    if (!reportPeriodId) return
    readinessService.getForPeriod(reportPeriodId)
      .then(response => setBlocking(response.findings.filter(f => f.severity === 'blocks_reporting')))
      .catch(() => setBlocking([]))
  }, [reportPeriodId, readinessService])

  if (blocking.length === 0) return null

  return (
    <Alert
      severity='warning'
      variant='outlined'
      icon={<InfoOutlined sx={{ color: 'text.primary' }} />}
      sx={{ mb: 3, p: 2, color: 'text.primary', borderColor: 'divider', borderRadius: '10px', '& .MuiAlert-message': { py: 0 }, '& .MuiAlert-action': { color: 'text.primary', alignItems: 'center', pt: 0 } }}
      action={
        <Button color='inherit' size='small' onClick={() => navigate('/readiness')}>
          <Typography variant='body2'>Review readiness</Typography>
        </Button>
      }
    >
      <Stack spacing={0.25}>
        <Typography variant='body2' sx={{ fontWeight: 600 }}>
          {blocking.length} critical readiness {blocking.length === 1 ? 'finding is' : 'findings are'} open for this period.
        </Typography>
        {blocking.slice(0, 3).map(finding => (
          <Typography key={`${finding.checkId}|${finding.entityKey}`} variant='body2'>{finding.title}</Typography>
        ))}
        {blocking.length > 3 &&
          <Typography variant='body2' sx={{ color: 'text.secondary' }}>and {blocking.length - 3} more</Typography>}
        <Typography variant='body2' sx={{ color: 'text.secondary' }}>
          You can still generate reports; they will carry these gaps until the data is fixed or the findings are acknowledged.
        </Typography>
      </Stack>
    </Alert>
  )
}
